import React from 'react';
import { useOverview } from '../../hooks/useAnalytics';
import { 
  KpiTile, 
  KpiGridSkeleton, 
  ErrorState,
  EmptyState
} from './AnalyticsShared';

export function OverviewTab({ range }) {
  const { data, isLoading, error, refetch } = useOverview(range);

  if (isLoading) {
    return <KpiGridSkeleton count={9} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  if (!data) {
    return (
      <EmptyState
        title="No data available"
        description="Analytics data will appear here once you have listing activity."
      />
    );
  }

  return (
    <div className="space-y-8">
      {/* Top Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Views */}
        <KpiTile
          title="Total Views"
          value={data.views}
          delta={data.views_delta}
          tooltip="Number of times your listings were viewed"
          format="number"
        />

        {/* Clicks */}
        <KpiTile
          title="Total Clicks"
          value={data.clicks}
          delta={data.clicks_delta}
          tooltip="Number of clicks on your listings"
          format="number"
        />

        {/* CTR */}
        <KpiTile
          title="Click-Through Rate"
          value={data.ctr}
          delta={data.ctr_delta}
          tooltip="Percentage of views that resulted in clicks"
          format="percent"
        />

        {/* Messages */}
        <KpiTile
          title="Messages Sent"
          value={data.messages_sent}
          delta={data.messages_sent_delta}
          tooltip="Number of messages sent to you by potential buyers"
          format="number"
        />

        {/* Orders */}
        <KpiTile
          title="Orders Created"
          value={data.orders_created}
          delta={data.orders_created_delta}
          tooltip="Number of new orders placed"
          format="number"
        />

        {/* Revenue */}
        <KpiTile
          title="Revenue"
          value={data.revenue_cents}
          delta={data.revenue_delta}
          tooltip="Total revenue from completed orders"
          format="currency"
        />

        {/* Conversion Rate */}
        <KpiTile
          title="View → Order Rate"
          value={data.view_to_order_rate}
          delta={data.view_to_order_rate_delta}
          tooltip="Percentage of views that resulted in orders"
          format="percent"
        />

        {/* Avg Order Value */}
        <KpiTile
          title="Avg Order Value"
          value={data.avg_order_value_cents}
          tooltip="Average revenue per order"
          format="currency"
        />

        {/* Orders Completed */}
        <KpiTile
          title="Orders Completed"
          value={data.orders_completed}
          delta={data.orders_completed_delta}
          tooltip="Number of orders successfully completed"
          format="number"
        />
      </div>

      {/* Sponsored Listings Section */}
      {data.has_ad_data && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Sponsored Listings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <KpiTile
              title="Ad Impressions"
              value={data.ad_impressions}
              delta={data.ad_impressions_delta}
              tooltip="Number of times your ads were shown"
              format="number"
            />

            <KpiTile
              title="Ad Clicks"
              value={data.ad_clicks}
              delta={data.ad_clicks_delta}
              tooltip="Number of clicks on your sponsored listings"
              format="number"
            />

            <KpiTile
              title="Ad CTR"
              value={data.ad_ctr}
              delta={data.ad_ctr_delta}
              tooltip="Click-through rate for sponsored listings"
              format="percent"
            />

            <KpiTile
              title="Ad Spend"
              value={data.ad_spend_cents}
              delta={data.ad_spend_delta}
              tooltip="Total amount spent on sponsored listings"
              format="currency"
            />

            <KpiTile
              title="Ad CPC"
              value={data.ad_cpc_cents}
              tooltip="Average cost per click"
              format="currency"
            />

            <KpiTile
              title="Ad Orders"
              value={data.ad_orders}
              delta={data.ad_orders_delta}
              tooltip="Orders attributed to sponsored listings"
              format="number"
            />

            <KpiTile
              title="Ad ROAS"
              value={data.ad_roas}
              tooltip="Return on ad spend (Revenue / Spend)"
              format="number"
              suffix="x"
            />

            <KpiTile
              title="Ad CPA"
              value={data.ad_cpa_cents}
              tooltip="Cost per acquisition (Spend / Orders)"
              format="currency"
            />
          </div>
        </div>
      )}
    </div>
  );
}
